import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import { ReactComponent as LeftArrowIcon } from 'Assets/svg/left-arrow-thicker.svg';
import { ReactComponent as CSVIcon } from 'Assets/svg/csv-icon.svg';
import { ReactComponent as FolderIcon } from 'Assets/svg/folder-open.svg';
import { ReactComponent as SuccessIcon } from 'Assets/svg/tick-icon-new.svg';
import { ReactComponent as ErrorIcon } from 'Assets/svg/warning-icon-new.svg';
import { ReactComponent as WarningIcon } from 'Assets/svg/warning-bg.svg';
import { ReactComponent as DownloadIcon } from 'Assets/svg/downloads-down.svg';
import { ReactComponent as EnrichmentIcon } from 'Assets/svg/enrichment.svg';
import SampleCSVEnrichmentData from 'Assets/csv/csvEnrichmentSample.csv';
import { warningPopupState } from 'Components/common/commonWarningPopup';
import Pagination from 'Components/common/pagination';
import { LoadingWrapper } from 'Components/common/loadingWrapper';
import { HocPopupComponent } from 'Components/common/hocPopupComponent';
import enrichmentState, {
  adaptFieldsForCSVEnrichment,
} from 'Stores/enrichment';
import { userDetail } from 'Stores/userDetail';
import { Utils } from 'Utils/utils';
import { EMPTY_FUNCTION, MX_ENRICHMENT_EVENTS } from 'Utils/constants';
import CSVEnrichmentInitiatedPopup from './csvEnrichmentInitiatedPopup';
import CSVFieldMappingPopup from './csvFieldMappingPopup';
import FileEnrichmentReport from './FileEnrichmentReport/fileEnrichmentReport';
import './styles.scss';

const BackButton = ({ title }) => {
  return (
    <div className="back-link-wrapper">
      <Link to="/enrichment">
        <span className="icon-wrap">
          <LeftArrowIcon className="left-arrow" />
        </span>
      </Link>
      <h1>{title}</h1>
    </div>
  );
};

const RenderLogField = data => {
  const {
    fileName,
    uploadedOn,
    totalRecord,
    enrichedRecord,
    creditUsed,
    status,
    id,
    className = '',
    enrichedCsvFile,
    setEnrichmentReportId,
  } = data;

  const isPartialOrTotallyComplete =
    status === 'COMPLETED' || status === 'PARTIALLY_COMPLETED';

  const isToBeProcessed =
      status === 'TO_BE_PROCESSED' || status === 'DATA_REQUESTED' || status === 'ENRICHED_DATA_RECEIVED';

  return (
    <div key={id} className={`log-field ${className}`.trim()}>
      <div className="log-field-inner">
        <span className="file-name show-elipsis" title={fileName}>
          {fileName}
        </span>
        <span className="uploaded-on">
          {uploadedOn !== null ? Utils.getFormattedDate(uploadedOn, true) : '-'}
        </span>
        <span className="total-count">
          {totalRecord !== null ? Utils.numberFormat(totalRecord) : '-'}
        </span>
        <span className="enriched-count">
          {enrichedRecord !== null && !isToBeProcessed
            ? Utils.numberFormat(enrichedRecord)
            : '-'}
        </span>
        <span className="credits-used">
          {creditUsed !== null && !isToBeProcessed
            ? Utils.numberFormat(creditUsed)
            : '-'}
        </span>
        <span className={`enrichment-status ${status}`}>
          {isPartialOrTotallyComplete ? (
            <SuccessIcon />
          ) : isToBeProcessed ? (
            <span className="loader">
              <span className="load-text" />
            </span>
          ) : (
            <ErrorIcon />
          )}
          <span>
            {isToBeProcessed
              ? 'Processing'
              : isPartialOrTotallyComplete
              ? 'Complete'
              : status.toLowerCase()}
          </span>
        </span>
        <span className="action">
          {isPartialOrTotallyComplete && enrichedRecord > 0 ? (
            <>
              <button
                type="button"
                className="view-report"
                onClick={() => {
                  Utils.mixpanelTrack(
                    MX_ENRICHMENT_EVENTS.VIEW_REPORT_BUTTON_CLICKED,
                    { enrichedCount: enrichedRecord },
                  );
                  setEnrichmentReportId(id);
                }}
              >
                View Report
              </button>
              <a
                type="file"
                href={enrichedCsvFile}
                target="_blank"
                rel="noreferrer"
                onClick={() => {
                  Utils.mixpanelTrack(
                    MX_ENRICHMENT_EVENTS.DOWNLOAD_BUTTON_CLICKED,
                    { enrichedCount: enrichedRecord },
                  );
                }}
              >
                <DownloadIcon />
                Download
              </a>
            </>
          ) : null}
        </span>
      </div>
    </div>
  );
};

const UploadFile = props => {
  const {
    processFile,
    onFileUpload,
    uploadText = 'Upload file',
    setShowUploadPopup = EMPTY_FUNCTION,
  } = props;
  return (
    <section
      className="upload-csv-wrapper"
      onDragEnter={e => {
        e.preventDefault();
        e.target.classList.add('hover-effect');
      }}
      onDragOver={e => e.preventDefault()}
      onDragLeave={e => {
        e.preventDefault();
        e.target.classList.remove('hover-effect');
      }}
      onDrop={e => {
        e.persist();
        e.preventDefault();
        setShowUploadPopup(false);
        e.target.classList.remove('hover-effect');
        const file = e.dataTransfer.files[0];
        if (file.type === 'text/csv') {
          processFile(file, 'dragndrop');
        } else {
          Utils.mixpanelTrack(MX_ENRICHMENT_EVENTS.ERROR_POPUP_SHOWN, {
            type: 'Invalid Format',
          });
          warningPopupState.setShowPopup({
            Icon: WarningIcon,
            className: 'file-enrichment-warning-popup invalid-format',
            title: 'Invalid File Format',
            message: 'Please select a CSV file and try again.',
          });
        }
      }}
    >
      <CSVIcon className="csv-icon" />
      <div className="upload-title">Drop your CSV file here or</div>
      <div className="upload-button-wrapper">
        <label htmlFor="upload-csv" className="csv-upload-label">
          <FolderIcon width="14" height="14" className="folder-icon" />
          {uploadText}
          <input
            className="csv-upload"
            type="file"
            id="upload-csv"
            accept=".csv"
            onChange={e => {
              setShowUploadPopup(false);
              onFileUpload(e, 'fileChooser');
            }}
          />
        </label>
      </div>
      <div className="enrichment-instructions">
        Please make sure to include
        <span className="highlight">First Name, Last Name, Domain.</span>
        If they are not available try adding
        <span className="highlight">Email Ids or LinkedIn URLs</span>and we will
        find the rest.{' '}
        <a href={SampleCSVEnrichmentData} onClick={e => e.stopPropagation()}>
          Sample File
        </a>
      </div>
      <div className="note">
        <span className="span-mr note-title">Note:</span>
        <span>
          Contact Information will not be enriched for Disposable, Personal
          emails & Group Email addresses.
        </span>
      </div>
    </section>
  );
};

const FileEnrichment = observer(() => {
  const [showFieldMappingPopup, togglePopup] = React.useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 10 });
  const [isListLoading, setIsListLoading] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [showUploadPopup, setShowUploadPopup] = useState(false);
  const [enrichmentReportId, setEnrichmentReportId] = useState('');
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const scrollArea = useRef();

  const processFile = (file, type) => {
    if (userDetail.userInfo.enrichmentCreditsRemaining > 0) {
      if (file) {
        const fileReader = new FileReader();
        fileReader.readAsText(file);
        fileReader.onload = async function(e) {
          const { result } = e.target;
          if (result.split('\n').length <= 25000 + 1) {
            Utils.mixpanelTrack(
              MX_ENRICHMENT_EVENTS.FILE_ENRICHMENT_FILE_UPLOAD,
              {
                type,
              },
            );
            const csvHeaderNames = result
              .split('\n')?.[0]
              ?.split(',')
              ?.map(data => data.trim());
            if (csvHeaderNames?.length > 0) {
              enrichmentState.fileEnrichmentCSVFields = csvHeaderNames;
              const twoDarrayOfData = result
                .split('\n')
                ?.splice(1)
                ?.map(data => data.split(','));
              const sampleData = csvHeaderNames.reduce((output, input) => {
                const index = csvHeaderNames.findIndex(
                  label =>
                    label?.toLowerCase()?.replace(/\s/g, '') ===
                    input.toLowerCase()?.replace(/\s/g, ''),
                );
                const sampleValue = twoDarrayOfData.find(
                  data => !!data[index],
                )?.[index];
                return { ...output, [input]: sampleValue };
              }, {});

              enrichmentState.fileEnrichmentSampleData = sampleData;
              enrichmentState.setFileEnrichmentFieldMapping(
                adaptFieldsForCSVEnrichment.map(data => {
                  const csvLabel = csvHeaderNames.find(
                    label =>
                      label?.toLowerCase()?.replace(/\s/g, '') ===
                      data.label.toLowerCase()?.replace(/\s/g, ''),
                  );
                  return {
                    adaptKey: data.value,
                    adaptLabel: data.label,
                    csvLabel,
                    sampleData: sampleData[csvLabel],
                  };
                }),
              );
            }
            const formData = new FormData();
            formData.append('csvFile', file);
            setShowLoader(true);
            const isSuccess = await enrichmentState.uploadCSV({
              postBody: formData,
            });
            setShowLoader(false);
            if (isSuccess) {
              togglePopup(true);
            } else {
              Utils.mixpanelTrack(MX_ENRICHMENT_EVENTS.ERROR_POPUP_SHOWN, {
                type: 'Upload Generic Error',
              });
              warningPopupState.setShowPopup({
                Icon: WarningIcon,
                className: 'file-enrichment-warning-popup',
                title: 'Oops! Something went wrong',
                message: (
                  <>
                    Please refresh the page and try again. If you face the error
                    again, write to us at{' '}
                    <a href="mailto:support@adapt.io">support@adapt.io</a>.
                  </>
                ),
              });
            }
          } else {
            Utils.mixpanelTrack(MX_ENRICHMENT_EVENTS.ERROR_POPUP_SHOWN, {
              type: 'Limit Reached',
            });
            warningPopupState.setShowPopup({
              Icon: WarningIcon,
              className: 'file-enrichment-warning-popup limit-exceeded',
              title: 'File Limit Exceeded',
              message:
                'Please select a file that has 25,000 or less records and try again.',
            });
          }
        };
      }
    } else {
      Utils.mixpanelTrack(MX_ENRICHMENT_EVENTS.ERROR_POPUP_SHOWN, {
        type: 'No Credits',
      });
      warningPopupState.setShowPopup({
        Icon: WarningIcon,
        className: 'file-enrichment-warning-popup no-credits',
        title: 'No Enrichment Credits left!',
        message: (
          <>
            You ran out of enrichment credits and kindly write to{' '}
            <a href={`mailto:${Utils.getSupportEmail()}`}>
              {Utils.getSupportEmail()}
            </a>{' '}
            for more credits
          </>
        ),
      });
    }
  };

  const onFileUpload = (event, type) => {
    event.persist();
    const file = event.target.files?.[0];
    processFile(file, type);
    // eslint-disable-next-line no-param-reassign
    event.target.value = '';
  };

  const changePage = page => {
    setPagination({ limit: pagination.limit, page: page + 1 });
  };

  const changePageLimit = limit => {
    setPagination({ page: 1, limit });
  };

  const getFileEnrichmentList = async () => {
    setIsListLoading(true);
    await enrichmentState.getFileEnrichmentList(pagination);
    if (scrollArea.current) scrollArea.current.scrollTop = 0;
    setIsListLoading(false);
    setIsInitialLoad(false);
  };

  useEffect(() => {
    getFileEnrichmentList();
  }, [pagination]);

  useEffect(() => {
    Utils.mixpanelTrack(MX_ENRICHMENT_EVENTS.FILE_ENRICHMENT_VIEWED);
    return () => {
      enrichmentState.fileEnrichmentList = [];
      enrichmentState.fileEnrichmentListCount = 0;
    };
  }, []);

  return (
    <div
      className={`file-enrichment-view${
        enrichmentState.fileEnrichmentListCount === 0 ||
        (isListLoading && isInitialLoad)
          ? ' empty-state'
          : ''
      }`}
    >
      {showFieldMappingPopup && (
        <CSVFieldMappingPopup
          pagination={pagination}
          togglePopup={() => togglePopup(false)}
        />
      )}
      {enrichmentState.showFileEnrichmentInitiatedPopup && (
        <CSVEnrichmentInitiatedPopup
          togglePopup={() => {
            enrichmentState.showFileEnrichmentInitiatedPopup = false;
          }}
        />
      )}
      {showUploadPopup && (
        <HocPopupComponent
          customClassName="upload-file-popup"
          closeClick={() => setShowUploadPopup(false)}
        >
          <UploadFile
            setShowUploadPopup={setShowUploadPopup}
            onFileUpload={onFileUpload}
            processFile={processFile}
          />
        </HocPopupComponent>
      )}
      <LoadingWrapper
        show={showLoader || (isListLoading && isInitialLoad)}
        position="fixed"
      />
      {enrichmentReportId !== '' ? (
        <FileEnrichmentReport
          backAction={() => setEnrichmentReportId('')}
          data={enrichmentState.fileEnrichmentList.find(
            list => list.id === enrichmentReportId,
          )}
        />
      ) : (
        <>
          <div className="title-wrapper">
            <BackButton title="CSV Enrichment" />
            {enrichmentState.fileEnrichmentListCount > 5 && (
              <div className="upload-button-wrapper">
                <button
                  type="button"
                  className="csv-upload-label"
                  onClick={() => {
                    setShowUploadPopup(true);
                  }}
                >
                  <FolderIcon width="14" height="14" className="folder-icon" />
                  Upload file
                </button>
              </div>
            )}
          </div>
          {(enrichmentState.fileEnrichmentListCount <= 5 ||
            enrichmentState.fileEnrichmentListCount === 0) && (
            <UploadFile onFileUpload={onFileUpload} processFile={processFile} />
          )}
          {enrichmentState.fileEnrichmentListCount === 0 && (
            <section className="info-card">
              <EnrichmentIcon />
              <div className="message">
                Adapt will enrich your file with additional data attributes like
                job title, mobile number, company name, and more.
              </div>
            </section>
          )}
          {enrichmentState.fileEnrichmentList.length > 0 && (
            <div className="table-wrapper">
              <div className="table-header">
                <div className="header-inner-wrap">
                  <span className="file-name">File Name</span>
                  <span className="uploaded-on">Uploaded Date</span>
                  <span className="total-count">Total Count</span>
                  <span className="enriched-count">Enriched Count</span>
                  <span className="credits-used">Credits Used</span>
                  <span className="enrichment-status">Enrichment Status</span>
                  <span className="action">Action</span>
                </div>
              </div>
              <div className="table-body" ref={scrollArea}>
                <div>
                  {isListLoading
                    ? Array.from({ length: 10 }).map((item, index) => {
                        return (
                          <div
                            key={`log-placeholder-${index}`}
                            className="log-field placeholder"
                          >
                            <div className="log-field-inner">
                              <span className="file-name linear-background" />
                              <span className="uploaded-on linear-background" />
                              <span className="total-count linear-background" />
                              <span className="enriched-count linear-background" />
                              <span className="credits-used linear-background" />
                              <span className="enrichment-status linear-background" />
                              <div className="action">
                                <span className="action linear-background" />
                                <span className="action linear-background" />
                              </div>
                            </div>
                          </div>
                        );
                      })
                    : enrichmentState.fileEnrichmentList.map(enrichmentData => {
                        return (
                          <RenderLogField
                            key={enrichmentData.id}
                            setEnrichmentReportId={setEnrichmentReportId}
                            {...enrichmentData}
                          />
                        );
                      })}
                </div>
              </div>
            </div>
          )}
          <Pagination
            totalCount={enrichmentState.fileEnrichmentListCount}
            rowsPerPage={pagination.limit}
            currentPage={pagination.page}
            updateCurrentPage={changePage}
            onRowsPerPageUpdate={changePageLimit}
            rowsPerPageValues={[10, 25, 50, 100].map(data => {
              return { label: data.toString(), value: data };
            })}
          />
        </>
      )}
    </div>
  );
});

export { BackButton };

export default FileEnrichment;
